import clsx from 'clsx';
import type React from 'react';
import type { ComponentProps } from 'react';
import type { PrefetchMode } from '@//hooks/useLinkPrefetch';
import { AppLink } from '../AppLink';
import { Spinner } from '../Spinner';
import styles from './Button.module.scss';

type NextLinkPropsSubset = Pick<ComponentProps<typeof AppLink>, 'replace' | 'scroll' | 'locale'>;

type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'cta'
  | 'inverted'
  | 'no-bg'
  | 'outline'
  | 'outline-inverted'
  | 'critical';

interface SharedProps {
  size?: ButtonSize;
  sizeLtMd?: ButtonSize;
  sizeMd?: ButtonSize;
  variant?: ButtonVariant;
  fullWidth?: boolean;
  fullWidthLtMd?: boolean;
  noWrap?: boolean;
  testId?: string;
  hoverGhost?: boolean;
}

type ButtonProps = SharedProps &
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    href?: undefined; // Disallow href for button
    loading?: boolean;
  };

type LinkProps = SharedProps &
  React.AnchorHTMLAttributes<HTMLAnchorElement> &
  NextLinkPropsSubset & {
    href: string;
    /** Prefetch policy. Default 'none' — explicit opt-in required. */
    prefetch?: PrefetchMode;
  };

export type ButtonComponentProps = ButtonProps | LinkProps;

export const Button = (props: ButtonComponentProps) => {
  const {
    size = 'md',
    sizeLtMd,
    sizeMd,
    variant = 'primary',
    fullWidth,
    fullWidthLtMd,
    noWrap,
    testId,
    className,
    children,
    hoverGhost,
    ...rest
  } = props;

  const classNames = clsx(styles.button, className, {
    [styles[`size--${size}`]]: size,
    [styles[`sizeMd--${sizeMd}`]]: sizeMd,
    [styles[`sizeLtMd--${sizeLtMd}`]]: sizeLtMd,
    [styles.variantPrimary]: variant === 'primary',
    [styles.variantSecondary]: variant === 'secondary',
    [styles.variantCta]: variant === 'cta',
    [styles.variantInverted]: variant === 'inverted',
    [styles.variantNoBg]: variant === 'no-bg',
    [styles.variantOutline]: variant === 'outline',
    [styles.variantOutlineInverted]: variant === 'outline-inverted',
    [styles.variantCritical]: variant === 'critical',
    [styles.fullWidth]: fullWidth,
    [styles.fullWidthLtMd]: fullWidthLtMd,
    [styles.noWrap]: noWrap,
    [styles.hoverGhost]: hoverGhost,
  });

  if ('href' in props && props.href) {
    const { href, replace, scroll, locale, prefetch, ...anchorProps } = rest as LinkProps;
    return (
      <AppLink
        href={href}
        replace={replace}
        scroll={scroll}
        locale={locale}
        prefetch={prefetch}
        className={classNames}
        data-testid={testId}
        {...anchorProps}
      >
        {children}
      </AppLink>
    );
  }

  const { loading, ...buttonProps } = rest as ButtonProps;
  return (
    <button
      type={buttonProps.type || 'button'}
      className={classNames}
      data-testid={testId}
      disabled={loading || buttonProps.disabled}
      {...buttonProps}
    >
      {children}
      {loading && <Spinner size="text" />}
    </button>
  );
};
